import { axiosLasta } from 'config';
import { setSeconds } from 'date-fns';
import { buildResponseUnauthorizedErrorHandler } from 'utils/axiosHelper/axiosErrorHandlerHelper.js';
import { DEFAULT_PAGING, INVENTORY_VIEW_MODE } from 'utils/constants';
import { toQueryString } from '../utils/queryStringHelpers';
import {
    buildInventoryItemTypeColumnsCacheName,
    buildInventoryItemTypeCacheName,
    buildInventoryItemTypeFormFieldsCacheName,
} from 'services/constants/cacheNameConstants';

const BASE_URL = '/inventory-source-data';

export const fetchInventory = async ({ locationId, selectedDate, viewMode }) => {
    const query = toQueryString({
        date: selectedDate ? setSeconds(selectedDate, 0).toISOString() : undefined,
        size: DEFAULT_PAGING.PAGE_SIZE,
    });

    let url = '';
    if (selectedDate) {
        url =
            viewMode === INVENTORY_VIEW_MODE.SHORT
                ? `${BASE_URL}/snapshot/location/${locationId}?${query}`
                : `${BASE_URL}/snapshot/location/${locationId}/fulldata?${query}`;
    } else {
        url = `${BASE_URL}/location/${locationId}`;
    }

    const response = await axiosLasta(url, {
        handleResponseError: buildResponseUnauthorizedErrorHandler("You don't have permission to access Inventory List."),
    });
    return response?.data ?? [];
};

export const fetchInventoryTableData = async ({ locationId }) => {
    const response = await axiosLasta(`${BASE_URL}/location/${locationId}/table`, {
        handleResponseError: buildResponseUnauthorizedErrorHandler("You don't have permission to access Inventory List."),
    });
    return response?.data ?? [];
};

export const fetchInventoryFormFieldsByLocation = async ({ locationId }) => {
    const response = await axiosLasta.get(`/inventory-item-types/form-fields/location/${locationId}`, {
        handleResponseError: buildResponseUnauthorizedErrorHandler("You don't have permission to access Inventory Fields."),
        cache: {
            enabled: true,
            key: buildInventoryItemTypeFormFieldsCacheName(locationId),
        },
    });
    return response?.data?.fields;
};

export const fetchInventoryColumnsByLocation = async ({ locationId }) => {
    const response = await axiosLasta.get(`/inventory-item-types/columns`, {
        handleResponseError: buildResponseUnauthorizedErrorHandler("You don't have permission to access Inventory Columns."),
        params: {
            locationId,
        },
        cache: {
            enabled: true,
            key: buildInventoryItemTypeColumnsCacheName(locationId),
        },
    });
    return response?.data?.fields;
};

export const fetchInventoryTypeByLocation = async (locationId) => {
    const response = await axiosLasta.get(`/inventory-item-types/location/${locationId}`, {
        handleResponseError: buildResponseUnauthorizedErrorHandler("You don't have permission to access Inventory Type List."),
        cache: {
            enabled: true,
            key: buildInventoryItemTypeCacheName(locationId),
        },
    });
    return response?.data;
};

export const createInventory = async (payload) => {
    const response = await axiosLasta.post(`/inventory-source-data`, payload);
    const { data } = response;
    return data;
};

export const editInventory = async (payload, id) => {
    const response = await axiosLasta.put(`/inventory-source-data`, payload);
    const { data } = response;
    return data;
};

export const removeInventory = async (id) => {
    const response = await axiosLasta.delete(`${BASE_URL}/${id}`);
    const { data } = response;
    return data;
};

export const fetchInventoryByEquipmentCategoryIdAndLocationId = async ({ equipmentCategoryId, locationId }) => {
    const response = await axiosLasta.get(`/inventory-source-data/location/${locationId}/equipment-category/${equipmentCategoryId}`, {
        handleResponseError: buildResponseUnauthorizedErrorHandler("You don't have permission to access Inventory List."),
    });
    return response?.data ?? [];
};

export const fetchInventoryArchiveCutoff = async () => {
    const response = await axiosLasta.get(`${BASE_URL}/archive-cutoff`, {
        handleResponseError: buildResponseUnauthorizedErrorHandler("You don't have permission to access Inventory List."),
    });
    return response?.data?.cutoff ?? null;
};

export const fetchInventorySnapshotDetail = async ({ locationId, inventoryItemTypeId, referenceId, selectedDate }) => {
    const query = toQueryString({
        inventoryItemTypeId,
        referenceId,
        date: selectedDate ? setSeconds(selectedDate, 0).toISOString() : undefined,
    });
    const response = await axiosLasta.get(`${BASE_URL}/snapshot/location/${locationId}/inventory/detail?${query}`, {
        handleResponseError: buildResponseUnauthorizedErrorHandler("You don't have permission to access Inventory Fields."),
    });
    return response?.data ?? [];
};
